// DateTimePickerField — reusable MUI X DateTimePicker для React Hook Form.
// Локаль: ru (date-fns/locale). Формат: dd.MM.yyyy HH:mm.
// Сериализует в ISO Instant (с timezone offset).
import type { ReactNode } from 'react';
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';
import { DateTimePicker } from '@mui/x-date-pickers';
import { TextField } from '@mui/material';

interface DateTimePickerFieldProps<T extends FieldValues> {
  /** Имя поля в форме. */
  name: Path<T>;
  /** RHF control. */
  control: Control<T>;
  /** Label поля. */
  label: string;
  /** Обязательное поле. */
  required?: boolean;
  /** Helper text под полем. */
  helperText?: string;
  /** Минимальная допустимая дата-время. */
  minDateTime?: 'now' | Date;
  /** Максимальная допустимая дата-время. */
  maxDateTime?: Date;
  /** Disabled. */
  disabled?: boolean;
  /** Полная ширина. */
  fullWidth?: boolean;
  /** ARIA-label для a11y. */
  'aria-label'?: string;
}

/** Преобразует Date в ISO Instant string. */
function toIsoInstant(d: Date | null): string | null {
  if (!d) return null;
  return d.toISOString();
}

/** Преобразует строку (ISO Instant) в Date для DateTimePicker. */
function parseFieldValue(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  const s = String(value);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Поле выбора даты+времени (MUI X DateTimePicker) для React Hook Form. */
export function DateTimePickerField<T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  helperText,
  minDateTime,
  maxDateTime,
  disabled = false,
  fullWidth = true,
  'aria-label': ariaLabel,
}: DateTimePickerFieldProps<T>): ReactNode {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const resolvedMinDateTime = minDateTime === 'now' ? new Date() : minDateTime;
        return (
        <DateTimePicker
          value={parseFieldValue(field.value)}
          onChange={(newValue: Date | null) => {
            field.onChange(toIsoInstant(newValue));
          }}
          format="dd.MM.yyyy HH:mm"
          {...(resolvedMinDateTime !== undefined && { minDateTime: resolvedMinDateTime })}
          {...(maxDateTime !== undefined && { maxDateTime })}
          disabled={disabled}
          ampm={false}
          slotProps={{
            textField: {
              label,
              required,
              fullWidth,
              error: Boolean(fieldState.error),
              helperText: fieldState.error?.message ?? helperText,
              inputProps: ariaLabel ? { 'aria-label': ariaLabel } : undefined,
            },
          }}
        />
        );
      }}
    />
  );
}